import { Component, Key } from 'react';

import {
    Card,
    message,
    Table,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { inject, observer } from 'mobx-react';
import { IReactionDisposer, reaction, toJS } from 'mobx';
import type { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import classNames from 'classnames';

import {
    AuthSettingsStore,
    UsersListStore,
    UserStore,
} from '@/stores';

import { ENDPOINTS, BASEURL } from '@/api';
import i18n from '@/content';
import { TableScrollProps, CognitoGroupsOrganizations } from '@/types/types';
import { captureErrorForSentry, checkIs404 } from '@/components/utils';

import EmailFilter from './EmailFilter';
import RoleFilter from './RoleFilter';
import {
    Email,
    DeleteUser,
    CreationDate,
    RoleGroup,
    PhoneNumber,
    Password,
} from './Cells';
import { OrganizationUser } from '@/stores/UsersListStore/interfaces';

const TABLE_SCROLL_OFFSET = '230px';

const { Column } = Table;

type AntFilterValue = Key | boolean ;

type Filters = Record<string, (Key | boolean)[] | null>;
type Sorter = SorterResult<OrganizationUser>;
type SorterUnion = SorterResult<OrganizationUser> | SorterResult<OrganizationUser>[];

interface UserRoleTableProps {
    userStore?: UserStore;
    authSettingsStore?: AuthSettingsStore;
    usersListStore?: UsersListStore;
}

interface UserRoleTableState {
    users: OrganizationUser[];
    filters: Filters;
    sorter: Sorter;
    loadingUserId: string | null;
}

@inject('authSettingsStore', 'userStore', 'usersListStore')
@observer
class UserRoleTable extends Component<UserRoleTableProps, UserRoleTableState> {
    private readonly nameSpace = 'userRoleEditor';

    private userListReaction: IReactionDisposer;

    constructor(props: UserRoleTableProps) {
        super(props);
        this.state = {
            users: [],
            filters: {},
            sorter: {},
            loadingUserId: null,
        };
    }

    componentDidMount(): void {
        const { usersListStore } = this.props;
        this.userListReaction = reaction<OrganizationUser[]>(
            () => usersListStore.usersList,
            (usersList) => this.setState({ users: toJS(usersList) }),
        );
    }

    componentWillUnmount(): void {
        this.userListReaction?.();
    }

    onChangeGroup = async (userId: string, group: CognitoGroupsOrganizations): Promise<void> => {
        const { usersListStore, authSettingsStore } = this.props;
        const { API } = authSettingsStore;
        const { nameSpace } = this;
        const { users } = this.state;

        // Set loading state and optimistically update the UI
        const updatedUsers = users.map<OrganizationUser>((userEntity) => {
            if (userEntity.user_id === userId) {
                return { ...userEntity, role: group };
            }
            return userEntity;
        });
        this.setState({ users: updatedUsers, loadingUserId: userId });

        try {
            await API.post(
                BASEURL.backend(),
                ENDPOINTS.changeOrganizationUserRole(),
                { body: { users: [{ userId, role: group }] } },
            );
            message.success(i18n.t(`${nameSpace}.changeRoleSuccess`));
        } catch (error) {
            captureErrorForSentry(error, 'UserRoleTable.onChangeGroup');
            message.error(i18n.t(`${nameSpace}.changeRoleError`));
            // Revert to original state on error
            await usersListStore.fetchUsersAndRoles();
        } finally {
            this.setState({ loadingUserId: null });
        }
    };

    private onDeleteSuccess(userId: string, email: string): void {
        const { nameSpace } = this;
        const { usersListStore: { removeUser } } = this.props;
        message.success(i18n.t(`${nameSpace}.deleteUserSuccess`, { email }));
        removeUser(userId);
        this.resetFilters();
    }

    deleteUser = async (userId: string, email: string): Promise<void> => {
        const { usersListStore, authSettingsStore, userStore } = this.props;
        const { isAdmin, isUserSetUp } = userStore;
        const { API } = authSettingsStore;
        const { nameSpace } = this;

        if (!(isAdmin && isUserSetUp)) {
            message.error(i18n.t(`${nameSpace}.cannotPerformOperation`));
            return;
        }

        try {
            usersListStore.setLoadingUserRoleEditor(true);
            await API.del(BASEURL.backend(), ENDPOINTS.deleteOrganizationUser(userId), {});
            await API.del(BASEURL.backend(), ENDPOINTS.deleteUser(userId), {});
            this.onDeleteSuccess(userId, email);
        } catch (error) {
            if (checkIs404(error)) {
                this.onDeleteSuccess(userId, email);
            } else {
                captureErrorForSentry(error, 'UserRoleTable.deleteUser');
                message.error(i18n.t(`${nameSpace}.deleteUserError`, { email }));
            }
        } finally {
            usersListStore.setLoadingUserRoleEditor(false);
        }
    };

    onChangeFilters = (pagination: TablePaginationConfig, filters: Filters, sorter: SorterUnion): void => {
        this.setState({ filters, sorter: sorter as Sorter });
        // Handle role filter change — fetch users based on selected roles
        if (filters.role) {
            const selectedRoles = (filters.role as string[]) || [];
            const rolesObject = {
                Administrators: selectedRoles.includes('Administrators'),
                Collaborators: selectedRoles.includes('Collaborators'),
                Auditors: selectedRoles.includes('Auditors'),
                Externals: selectedRoles.includes('Externals'),
            };
            this.props.usersListStore.fetchUsersAndRoles(rolesObject);
        }
    };

    private resetFilters(): void {
        this.setState({ filters: {}, sorter: {} });
    }

    render(): JSX.Element {
        const { nameSpace } = this;
        const {
            users,
            filters,
            sorter,
            loadingUserId,
        } = this.state;
        const searchText = filters.user_email?.[0] as string || '';
        const {
            userStore: { currentUserId },
            usersListStore: { cognitoGroupsList },
        } = this.props;
        const columnsNameSpace = `${nameSpace}.tableColumns`;
        const rolesSelectOptions = cognitoGroupsList.map((value) => (
            { value, label: i18n.t(`general.groups.${value}`) }
        ));
        const tableScroll: TableScrollProps = users?.length
            ? { y: `calc(100vh - ${TABLE_SCROLL_OFFSET})`, x: true }
            : {};

        return (
            <div className="tables-wrapper">
                <Card>
                    <Table
                        data-testid="userRoleEditor_usersTable"
                        rowKey="user_id"
                        size="middle"
                        className="users-table"
                        pagination={false}
                        dataSource={users}
                        scroll={tableScroll}
                        onChange={this.onChangeFilters}
                        tableLayout="fixed"
                    >
                        <Column
                            title={i18n.t(`${columnsNameSpace}.email`)}
                            dataIndex="user_email"
                            className={classNames('users-table-column', ' users-table-column-email')}
                            key="user_email"
                            defaultSortOrder="descend"
                            // this is required to reset filters
                            filteredValue={filters.user_email || null}
                            filterDropdown={({
                                setSelectedKeys, selectedKeys, confirm, clearFilters, visible, close,
                            }) => (
                                <EmailFilter
                                    visible={visible}
                                    selectedKeys={selectedKeys}
                                    setSelectedKeys={setSelectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    close={close}
                                />
                            )}
                            filterIcon={(filtered) => (
                                <SearchOutlined
                                    style={{ color: filtered ? '#1890ff' : undefined }}
                                />
                            )}
                            onFilter={(value: AntFilterValue, { user_email }: OrganizationUser) => (
                                user_email.toLowerCase().includes((value as string).toLowerCase())
                            )}
                            render={(cellValue) => (
                                <Email
                                    email={cellValue}
                                    searchText={searchText}
                                />
                            )}
                        />
                        <Column
                            title={i18n.t(`${columnsNameSpace}.roleGroup`)}
                            dataIndex="role"
                            key="role"
                            className={classNames('users-table-column', 'users-table-column-role')}
                            filterDropdown={({
                                setSelectedKeys, confirm,
                            }) => (
                                <RoleFilter
                                    selectedKeys={cognitoGroupsList}
                                    setSelectedKeys={setSelectedKeys}
                                    confirm={confirm}
                                />
                            )}
                            onFilter={(value: AntFilterValue, { role }: OrganizationUser) => (role === value)}
                            filteredValue={filters.role || null}
                            render={(text, record) => (
                                <RoleGroup
                                    userId={record.user_id}
                                    group={text}
                                    options={rolesSelectOptions}
                                    onChange={this.onChangeGroup}
                                    disabled={loadingUserId === record.user_id}
                                />
                            )}
                        />
                        <Column
                            title={i18n.t(`${columnsNameSpace}.phoneNumber`)}
                            dataIndex="user_email"
                            key="user_email"
                            className="users-table-column-phone"
                            render={(user_email) => <PhoneNumber email={user_email} />}
                        />
                        <Column
                            title={i18n.t(`${columnsNameSpace}.password`)}
                            dataIndex="user_email"
                            key="password"
                            className="users-table-column-password"
                            render={(user_email, record: OrganizationUser) => (
                                <Password
                                    email={user_email}
                                    userId={record.user_id}
                                />
                            )}
                        />
                        <Column
                            title={i18n.t(`${columnsNameSpace}.creationDate`)}
                            dataIndex="date_added"
                            key="date_added"
                            className="users-table-column-date"
                            sorter={(current: OrganizationUser, prev: OrganizationUser) => (
                                current.date_added - prev.date_added
                            )}
                            sortOrder={sorter.columnKey === 'date_added' ? sorter.order : null}
                            render={(text: number) => <CreationDate createDate={text} />}
                        />
                        <Column
                            className="users-table-column-del"
                            render={(text, { user_id: userId, user_email: email }: OrganizationUser) => {
                                const shouldDisplay = userId !== currentUserId;
                                return (
                                    <DeleteUser
                                        shouldDisplay={shouldDisplay}
                                        email={email}
                                        userId={userId}
                                        deleteUser={this.deleteUser}
                                    />
                                );
                            }}
                        />
                    </Table>
                </Card>
            </div>
        );
    }
}

export default UserRoleTable;
